import { isProspectingSuggestion } from "./services/task-routing.mjs";
import TaskDetails from "../components/TaskDetails";
import { taskActionResult } from "../utils/taskActionResult";
import { candidateName } from "../utils/candidateName";
import TaskCalendar from "./components/TaskCalendar";
import { interviewTasks } from "../utils/interviewTasks";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../components/reusableComponents/Dialog";
import axios from "../axios/AxiosInstance";
import { apiErrorMessage } from "../utils/apiError";
import TaskModal from "../components/reusableComponents/TaskModal";
import React, { useState, useEffect } from "react";
import {
  CalendarDays,
  ChevronRight,
  ChevronLeft,
  Plus,
  SlidersHorizontal,
  BarChart3,
  Clock3,
  ArrowUpRight,
} from "lucide-react";
import { formatDate } from "./crm.mjs";
import { searchEstablishments } from "./services/commercial.mjs";
import { businessDaysBetween } from "./utils/calendar.mjs";
import {
  userDailyState,
  getDailyGoals,
  periodIndicators,
} from "./services/daily.mjs";
import { DailyCard as Card, DailyGoals } from "./components/daily/DailyWidgets";
import { dashboardMetrics, shiftDate } from "./dashboard-metrics.mjs";
import { loadInterviews } from "../services/interviewApi";
const typeLabels = {
  call: "Appel",
  follow: "Relance",
  contact: "Contact",
  manager: "Suivi admin",
  appointment: "RDV",
  email: "Email",
  invoice: "Facture impayée",
};
export default function Dashboard({
  state,
  today,
  isHyperadmin,
  search,
  selectedId,
  onSelect,
  onTask,
  userId,
  onOpenClient,
  onOpenNeed,
  onNavigate,
  onNewTask,
  onGoal,
  onCompleteTask,
  dayContent,
  renderActivity,
  renderTasks,
}) {
  const [activityVisible, setActivityVisible] = useState(false);
  const [date, setDate] = useState(today),
    [taskFilter, setTaskFilter] = useState("todo"),
    [taskType, setTaskType] = useState("all"),
    [sort, setSort] = useState("priority"),
    [group, setGroup] = useState(null);
  useEffect(() => {
    if (group)
      document
        .querySelector(".dailySelection")
        ?.scrollIntoView({ block: "start" });
  }, [group]);
  state = userDailyState(state, userId);
  const metrics = dashboardMetrics(state, date, isHyperadmin);
  const goals = getDailyGoals(state, userId, date);
  const actual = periodIndicators(state, date, date);
  const [interviews, setInterviews] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [interviewError, setInterviewError] = useState("");
  const [apiTasks, setApiTasks] = useState([]);
  const [apiError, setApiError] = useState("");
  const [apiLoading, setApiLoading] = useState(false);
  const [apiRevision, setApiRevision] = useState(0);
  const [apiModal, setApiModal] = useState(null);
  const [apiDetail, setApiDetail] = useState(null);
  const [takers, setTakers] = useState([]);
  useEffect(() => {
    if (!dayContent) return;
    let active = true;
    setApiLoading(true);
    setApiError("");
    axios
      .get(`/newtask/find-by-date?date=${encodeURIComponent(date)}`)
      .then(({ data }) => {
        if (active) setApiTasks(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (active) {
          setApiTasks([]);
          setApiError(apiErrorMessage(error, "Impossible de charger les tâches enregistrées."));
        }
      })
      .finally(() => {
        if (active) setApiLoading(false);
      });
    return () => {
      active = false;
    };
  }, [date, dayContent, apiRevision]);
  useEffect(() => {
    if (!dayContent) return;
    let active = true;
    axios
      .get("newtask/recipients")
      .then(({ data }) => {
        if (active)
          setTakers(
            (Array.isArray(data) ? data : []).map((user) => ({
              value: user._id,
              label: `${[user.firstname, user.lastname].filter(Boolean).join(" ")} — ${user.role === "FREELANCE" ? "Freelance" : "Admin"}`,
              key: user._id,
            })),
          );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [dayContent]);
  useEffect(() => {
    if (!dayContent) return;
    const controller = new AbortController();
    let active = true;
    setInterviewError("");
    loadInterviews({ date, signal: controller.signal })
      .then((rows) => {
        if (active) {
          setInterviews(
            rows.map((row) => ({ ...row, person: row.candidate })),
          );
        }
      })
      .catch((error) => {
        const detail = apiErrorMessage(
          error,
          "Impossible de charger les entretiens.",
        );
        if (active && detail) setInterviewError(detail);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [date, dayContent]);
  useEffect(() => {
    if (!dayContent) return undefined;
    const refresh = () => setApiRevision(value => value + 1);
    const timer = setInterval(refresh, 20000);
    window.addEventListener("focus", refresh);
    return () => {clearInterval(timer);window.removeEventListener("focus", refresh);};
  }, [dayContent]);
  const externalTasks = apiTasks.map((task) => {
    const text = String(task.type || task.taskType || "").toLowerCase();
    const category = text.includes("appel")
      ? "call"
      : text.includes("relance")
        ? "follow"
        : text.includes("visite") ||
            text.includes("entretien") ||
            text.includes("rdv")
          ? "appointment"
          : text.includes("mail")
            ? "email"
            : "other";
    return {
      id: `api-${task._id || task.id}`,
      raw: task,
      category,
      title: String(task.title || task.comment || task.type || "Tâche"),
      time: task.hour || task.time,
      due_date_original: String(task.date || date).slice(0, 10),
      scheduled_date: String(task.date || date).slice(0, 10),
      clientId: task.clients?.[0]?._id || task.clients?.[0],
      done: task.status === "Effectuée",
      cancelled: task.status === "annulée",
      priority:
        { Urgent: "urgent", Haute: "high", Moyenne: "medium", Basse: "low" }[
          task.priority
        ] || task.priority,
    };
  });
  const completedTasks = state.tasks.filter(
    (task) =>
      !isProspectingSuggestion(task) &&
      task.done &&
      task.due_date_original === date &&
      (isHyperadmin || task.category !== "manager"),
  );
  const matchingTasks = [
    ...metrics.tasks,
    ...completedTasks,
    ...externalTasks,
    ...interviewTasks(interviews, date),
  ].filter((t) => {
    if (isProspectingSuggestion(t)) return false;
    const c = state.clients.find((c) => c.id === t.clientId);
    return (
      !search ||
      searchEstablishments(c ? [c] : [], search).length > 0 ||
      (!!search &&
        [
          t.title,
          ...(t.raw?.clients || []).map(
            (client) => client.companyName || client.name || "",
          ),
        ]
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase()))
    );
  });
  const filteredTasks = matchingTasks
    .filter(
      (t) =>
        (taskType === "all" || t.category === taskType) && (taskFilter === "all" ||
        (taskFilter === "todo"
          ? !t.done && !t.cancelled
          : taskFilter === "done"
            ? t.done
            : taskFilter === "cancelled"
              ? t.cancelled
              : taskFilter === "late"
                ? !t.done && !t.cancelled && t.due_date_original < date
                : t.category === taskFilter)),
    )
    .sort((a, b) =>
      sort === "date"
        ? a.due_date_original.localeCompare(b.due_date_original)
        : (a.due_date_original < date ? -1 : 0) -
            (b.due_date_original < date ? -1 : 0) ||
          ({ urgent: 4, high: 3, medium: 2, low: 1 }[b.priority] || 0) -
            ({ urgent: 4, high: 3, medium: 2, low: 1 }[a.priority] || 0) ||
          (state.clients.find((c) => c.id === b.clientId)?.score || 0) -
            (state.clients.find((c) => c.id === a.clientId)?.score || 0),
    );
  const attributionName = person => {
    if (!person) return "Non renseigné";
    if (typeof person === "object") {
      const name = [person.firstname, person.lastname].filter(Boolean).join(" ") || person.name || person.username;
      if (name) return name;
    }
    const id = String(person._id || person.id || person);
    return takers.find(item=>String(item.value)===id)?.label || state.users?.find(user=>String(user.id || user._id)===id)?.name || (id===String(userId) ? "Moi" : "Non renseigné");
  };
  const dateLabel = new Date(`${date}T12:00:00Z`).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric" });
  return (
    <div className="dashboard">
      <div className="dashboardIntro">
        <div>
          <p>Ma journée du {dateLabel}</p>
        </div>
        <div className="dateControls">
          <button
            type="button"
            className="secondary"
            aria-label="Suivi de mon activité"
            title={activityVisible ? "Masquer le suivi de mon activité" : "Afficher le suivi de mon activité"}
            aria-expanded={activityVisible}
            aria-controls="daily-activity-summary"
            onClick={() => setActivityVisible(visible => !visible)}
          >
            <BarChart3 aria-hidden="true" />
          </button>
          <button
            className="secondary"
            aria-label="Jour précédent"
            onClick={() => setDate((d) => shiftDate(d, -1))}
          >
            <ChevronLeft />
          </button>
          <label>
            <CalendarDays />
            <input lang="fr-FR"
              aria-label="Date de la journée"
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
            />
          </label>
          <button
            className="secondary"
            aria-label="Jour suivant"
            onClick={() => setDate((d) => shiftDate(d, 1))}
          >
            <ChevronRight />
          </button>
          <button className="secondary" onClick={() => setDate(today)}>
            Aujourd’hui
          </button>
        </div>
      </div>
      <div
        id="daily-activity-summary"
        className="dailyTopRow"
        hidden={!activityVisible}
        style={{ display: activityVisible ? "grid" : "none", gridTemplateColumns: "minmax(0, 1fr)" }}
      >
        {activityVisible && (renderActivity ? renderActivity(<DailyGoals goals={goals} actual={actual} onEdit={onGoal} embedded />) : <DailyGoals goals={goals} actual={actual} onEdit={onGoal} />)}
      </div>
      <div className="dashboardLayout">
        <div className="dashboardMain">
          {group && (
            <Card
              className="dailySelection"
              title={group.title}
              action={
                <button className="textAction" onClick={() => setGroup(null)}>
                  Fermer la sélection
                </button>
              }
            >
              {group.items.map((item) => (
                <button
                  className="dailyOpportunity"
                  key={item.id}
                  onClick={() =>
                    group.kind === "clients"
                      ? onOpenClient(item.id)
                      : group.kind === "engagements"
                        ? onTask(state.tasks.find((t) => t.id === item.taskId))
                        : onOpenNeed(
                            group.kind === "quotes" ? item.needId : item.id,
                          )
                  }
                >
                  <div>
                    <b>
                      {group.kind === "clients"
                        ? item.name
                        : state.clients.find((c) => c.id === item.clientId)
                            ?.name}
                    </b>
                    <small>{item.job || item.title || item.status}</small>
                  </div>
                  <ChevronRight />
                </button>
              ))}
              {!group.items.length && (
                <p className="dashboardEmpty">
                  Aucun élément pour cette sélection.
                </p>
              )}
            </Card>
          )}
          {metrics.overdue > 0 && (
            <button
              className="lateBanner"
              onClick={() => setTaskFilter("late")}
            >
              <Clock3 />
              <b>
                {metrics.overdue} tâche{metrics.overdue > 1 ? "s" : ""} en
                retard
              </b>
              <span>à traiter en priorité</span>
              <ChevronRight />
            </button>
          )}
          {renderTasks ? renderTasks(date, matchingTasks) : <Card
            title={`Mes tâches de la journée (${matchingTasks.length})`}
            action={
              <div className="dailyTaskHeaderActions flex flex-wrap items-center gap-2">
                <button
                  className="primary"
                  onClick={() => (dayContent ? setApiModal({}) : onNewTask())}
                >
                  <Plus />
                  Ajouter une tâche
                </button>
                <label title="Type d’action">
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  <span className="sr-only">Type d’action</span>
                  <select aria-label="Type d’action" value={taskType} onChange={event => setTaskType(event.target.value)}>
                    {[["all","Tous les types"],["call","Appels"],["follow","Relances"],["appointment","Rendez-vous"],["email","Emails"],["contact","Contacts"],["invoice","Factures impayées"],["other","Autres"]].map(([value,label])=><option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <TaskCalendar date={date} onChange={setDate} today={today} />
              </div>
            }
          >
            <p className="taskListSummary" role="status">
              <strong>{matchingTasks.filter(t=>!t.done&&!t.cancelled&&t.due_date_original<date).length} en retard</strong>
              <span>{matchingTasks.filter(t=>!t.done&&!t.cancelled&&t.due_date_original===date).length} à faire le {dateLabel}</span>
            </p>
            <div className="taskToolbar">
              <div className="taskFilters">
                {[
                  ["all", "Toutes"],
                  ["todo", "À faire"],
                  ["done", "Terminées"],
                  ["cancelled", "Annulées"],
                  ["late", "En retard"],
                ].map(([key, label]) => {
                  const count = matchingTasks.filter(
                    (t) =>
                      (taskType === "all" || t.category === taskType) && (key === "all" ||
                      (key === "todo"
                        ? !t.done && !t.cancelled
                        : key === "done"
                          ? t.done
                          : key === "cancelled"
                            ? t.cancelled
                            : key === "late"
                              ? !t.done &&
                                !t.cancelled &&
                                t.due_date_original < date
                              : t.category === key)),
                  ).length;
                  return (
                    <button
                      key={key}
                      className={taskFilter === key ? "active" : ""}
                      aria-pressed={taskFilter === key}
                      onClick={() => setTaskFilter(key)}
                    >
                      {label}
                      <span>{count}</span>
                    </button>
                  );
                })}
              </div>
              <select
                aria-label="Trier les tâches"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="priority">Tri : priorité</option>
                <option value="date">Tri : échéance</option>
              </select>
            </div>
            <div className="tableScroll">
              <table className="taskTable dailyTaskTable">
                <thead>
                  <tr>
                    <th>Priorité</th>
                    <th>Échéance</th>
                    <th>Client / Contact</th>
                    <th>À faire / Pourquoi ?</th>
                    <th>Attribution</th>
                    <th>Statut</th>
                    <th>Résultat de l’action</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((t) => {
                    const c = state.clients.find((c) => c.id === t.clientId),
                      late =
                        !t.done && !t.cancelled && t.due_date_original < date;
                    const priority = t.priority || (late ? "urgent" : c?.score >= 85 ? "high" : c?.score < 40 ? "low" : "normal");
                    return (
                      <tr
                        key={t.id}
                        className={c?.id === selectedId ? "selectedTask" : ""}
                      >
                        <td>
                          <span
                            className={`priority ${priority}`}
                          >
                            {t.priority
                              ? {
                                  urgent: "Urgente",
                                  high: "Haute",
                                  medium: "Moyenne",
                                  low: "Basse",
                                }[t.priority] || t.priority
                              : late
                                ? "Urgente"
                                : c?.score >= 85
                                  ? "Haute"
                                  : c?.score < 40
                                    ? "Basse"
                                    : "Normale"}
                          </span>
                        </td>
                        <td className="taskDeadline">
                          <b>{t.category === "invoice" && !t.invoiceDue ? "Non renseignée" : formatDate(t.due_date_original)}</b>
                          <small>{t.time || "Heure libre"}</small>
                        </td>
                        <td>
                          <button
                            className="clientLink"
                            disabled={!c}
                            onClick={() => {
                              onSelect(t.clientId);
                              onOpenClient(t.clientId);
                            }}
                          >
                            {c?.name ||
                              t.raw?.clients
                                ?.map(
                                  (client) => client.companyName || client.name,
                                )
                                .filter(Boolean)
                                .join(", ") ||
                              "—"}
                          </button>
                          <small>
                            {c?.city} ·{" "}
                            {c?.contacts.find((x) => x.active && x.primary)
                              ?.name || "Contact à rechercher"}
                          </small>
                          {(t.interview?.person || t.raw?.candidates?.length || t.candidates?.length) && <small>Intérimaire : {(t.interview ? [t.interview.person] : t.raw?.candidates || t.candidates || []).map(person=>candidateName(person)).filter(Boolean).join(", ")}</small>}
                        </td>
                        <td className="taskPurpose">
                          <span>{t.raw ? t.raw.title || t.raw.type || "Tâche" : t.title}</span>
                          <small>
                            {t.category === "invoice"
                              ? `${Number(t.remainingAmount).toLocaleString("fr-FR")} € restant à régler`
                              : t.category === "engagement"
                                ? "Engagement client"
                                : t.automationKey === "promised-call"
                                  ? "Rappel convenu avec le client"
                                  : t.automationKey === "prepare-quote"
                                    ? "Devis à préparer"
                                    : t.category === "need"
                                      ? "Besoin à qualifier"
                                      : t.quoteId || t.raw?.quotationReminderId || t.category === "quote"
                                        ? "Devis à relancer"
                                  : c?.riskReason
                                    ? "Client à risque"
                                    : state.signals.some(
                                          (s) =>
                                            s.clientId === c?.id &&
                                            s.status === "nouveau",
                                        )
                                      ? "Nouveau signal"
                                      : "Suivi commercial"}
                          </small>
                          {(t.raw?.comment || t.interview?.comment || t.comment || t.notes) && <details className="taskNote"><summary>Voir le commentaire</summary><p>{t.raw?.comment || t.interview?.comment || t.comment || t.notes}</p></details>}
                        </td>
                        <td className="taskAttribution">
                          <small>Attribuée par</small>
                          <span>{t.raw?.taskCreator || t.taskCreator || t.createdBy || t.creatorId ? attributionName(t.raw?.taskCreator || t.taskCreator || t.createdBy || t.creatorId) : t.automationKey || t.reactivationEngine ? "Automatisme" : "Non renseigné"}</span>
                          <small>Attribuée à</small>
                          <span>{[t.raw?.taskManagers || t.taskManagers || t.ownerId].flat().filter(Boolean).map(attributionName).join(", ") || "Non renseigné"}</span>
                        </td>
                        <td>
                          <span
                            className={`taskState ${late ? "late" : "due"}`}
                          >
                            {t.cancelled
                              ? "Annulée"
                              : t.done
                                ? "Terminée"
                                : late
                                  ? `En retard : ${businessDaysBetween(t.due_date_original, date, state.calendar.closures)} jours ouvrés`
                                  : "À faire"}
                            {late && (
                              <small>
                                Report : {formatDate(t.scheduled_date)}
                              </small>
                            )}
                          </span>
                        </td>
                        <td className="taskActionResult">
                          {taskActionResult(t, state.results) || <span className="text-slate-400">Non renseigné</span>}
                        </td>
                        <td>
                          <button
                            className="tableAction"
                            onClick={() =>
                              t.interview
                                ? setSelectedInterview(t)
                                : t.raw
                                  ? setApiDetail(t.raw)
                                  : onTask(t)
                            }
                          >
                            {t.interview
                              ? "Voir l’entretien"
                              : t.raw
                                ? "Voir la tâche"
                                : t.category === "call"
                                  ? "Saisir le résultat de mon appel"
                                  : t.category === "follow"
                                    ? "Saisir le rappel"
                                    : t.category === "quote" || t.quoteId
                                      ? "Ouvrir le devis"
                                      : t.category === "need"
                                        ? "Traiter le besoin"
                                        : t.category === "contact"
                                          ? "Compléter le contact"
                                          : "Traiter"}
                            <ArrowUpRight />
                          </button>
                          {!t.raw &&
                            !t.interview &&
                            [
                              "email",
                              "appointment",
                              "manager",
                              "engagement",
                            ].includes(t.category) && (
                              <button
                                className="doneAction"
                                onClick={() => onCompleteTask(t.id)}
                              >
                                Terminer
                              </button>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!filteredTasks.length && !apiLoading && !apiError && (
                <p className="dashboardEmpty">
                  Aucune tâche pour ce filtre. Les prochains suivis restent
                  visibles dans Agenda.
                </p>
              )}
            </div>
            <div className="cardFooter">
              <button
                className="textAction"
                onClick={() => onNavigate("follow")}
              >
                Tous les suivis <ChevronRight />
              </button>
            </div>
            {apiLoading && (
              <p role="status" className="dashboardEmpty">
                Chargement des tâches…
              </p>
            )}
            {apiError && (
              <p role="alert" className="dashboardEmpty">
                {apiError}
                <button
                  type="button"
                  onClick={() => setApiRevision((value) => value + 1)}
                >
                  Réessayer
                </button>
              </p>
            )}
            {interviewError && (
              <p role="alert" className="dashboardEmpty">
                {interviewError}
              </p>
            )}
            {selectedInterview && (
              <Dialog open onOpenChange={() => setSelectedInterview(null)}>
                <DialogContent className="bg-white rounded-2xl p-6">
                  <DialogTitle>{selectedInterview.title}</DialogTitle>
                  <DialogDescription>
                    {formatDate(selectedInterview.due_date_original)} à{" "}
                    {selectedInterview.time || "heure non renseignée"}
                  </DialogDescription>
                  <p className="mt-4 text-slate-700">
                    {selectedInterview.interview.place || "Lieu non renseigné"}
                  </p>
                  <p className="mt-2 text-slate-500">
                    {selectedInterview.interview.comment}
                  </p>
                  <button
                    className="secondary mt-4"
                    onClick={() => setSelectedInterview(null)}
                  >
                    Fermer
                  </button>
                </DialogContent>
              </Dialog>
            )}
            {apiDetail && <TaskDetails task={apiDetail} onClose={()=>setApiDetail(null)} onEdit={task=>{setApiDetail(null);setApiModal(task);}} onChanged={()=>setApiRevision(value=>value+1)} />}
            {apiModal && (
              <TaskModal
                open
                task={apiModal}
                takers={takers}
                isUpdating={Boolean(apiModal._id || apiModal.id)}
                onOpenChange={() => setApiModal(null)}
                updateOnCreate={() => {
                  setApiRevision((value) => value + 1);
                  setApiModal(null);
                }}
              />
            )}
          </Card>}
        </div>
      </div>
    </div>
  );
}
